// Auth context. Loads the current session on mount, exposes login/logout. The
// server is the source of truth; this just mirrors it for the UI.
//
// Session AEH (FIX-2 B1): resilience. Previously ANY /api/auth/me failure (a 429 from the rate
// limiter, a 5xx, or an offline blip) set user=null, and ProtectedRoute bounced the office to the
// Google sign-in and stranded an offline engineer at a login screen that needs the server. Now:
//   - only a real 401 signs the user out;
//   - a transient failure KEEPS the last-known user, flags a quiet connectivity notice, and
//     retries with exponential backoff;
//   - the last-known user is cached in localStorage (NOT the service worker, so the Session AH
//     diary-only SW exception is untouched), so an engineer's cold OFFLINE open hydrates straight
//     to My jobs instead of a login bounce.
// The cache is a UI hint only, never a credential: every API call still carries the httpOnly
// session cookie and the server enforces auth, so a tampered cache yields nothing (its calls 401
// and sign the user out).
//
// Session ASB (Item 3): sessions live on the server now, with idle and absolute limits, and can be
// ended from elsewhere. ANY request the guard refuses with 401 { error: "unauthorized", reason }
// (sessionEvents.ts onSessionEnded, reported by api.ts), not just /auth/me, signs this browser out
// locally with the SAME cleanup logout() runs, and remembers the reason so ProtectedRoute can send
// the person to their sign-in page with a plain-English message ("Signed out after inactivity").
// The engineer's queued work (the outboxes) and their unfinished job sheets are never part of that
// cleanup (offlineCache.ts), so nothing they had not sent is lost: it sends once they sign in again.
//
// WHICH SERVER-ENDED SESSIONS WIPE THE OFFLINE COPIES (Session ASB review, finding F1; narrowed by the third review).
// Only an ENGINEER's session that simply RAN OUT (idle_timeout, session_expired, or no_session, which is also what every
// pre-ASB cookie becomes at deploy) keeps them: the engineer's saved job list, with its site access details, is what the
// unguarded "Continue offline" view shows an engineer in a basement with no signal to sign in with. Everything else
// wipes them, exactly as a sign-out the person chose does: a session that was TAKEN AWAY (reason signed_out: an
// administrator's revoke, sign out everywhere, a deactivation, a password reset, a lost phone, or an account that was
// deleted), an ended auditor window, and ANY admin or office session, whatever the reason. An engineer session has no
// idle limit, so keeping on idle_timeout (or on the 12-hour session_expired) only ever applied to an admin driving the
// engineer app, and an admin's cached job list is the OVERSIGHT list (GET /api/jobs/mine): every active job's site
// address and access details, which an hourly idle-out left readable through "Continue offline" on an unlocked phone.
// The in-memory request cache goes either way (keepsOfflineCopies below is the one rule).
//
// KEPT COPIES EXPIRE (the third review). When copies are kept, the time is recorded beside their owner
// (OFFLINE_KEPT_AT_KEY below). If nobody has signed in within seven days, the sheet drafts' own retention
// (DRAFT_MAX_AGE_MS), they are wiped the next time the app starts (AuthProvider's mount) or the offline view opens
// (OfflineJobs, which waits for the wipe before it reads anything), and the hints go with them, but only once the wipe
// reports that it FINISHED (fix round 4): a wipe that fails part way (an IndexedDB connection WebKit dropped while the
// phone sat in the background) keeps the hints, so the next start or the next opening of the offline view tries again.
// This is what disposes of copies kept on a phone whose engineer is later deleted or deactivated: the signed_out the
// server then sends can only reach a phone that is still signed in (the session-ended listener acts only while somebody
// is), and a phone that has already signed out makes no request whose reason it acts on.
//
// COPIES THAT CARRY NO HINT AT ALL GET THE SAME CLOCK (fix round 4). A phone an earlier build signed out (a pre-ASB
// cookie that lapsed, or an engineer deactivated before the deploy, a departed engineer's among them) holds its saved job
// list with no owner and no time, and nothing above would ever expire it. So when the app starts or the offline view
// opens with NOBODY signed in on this browser, no hints and a saved job list, the time is recorded, with no owner, and
// those copies expire seven days later like any others (the password form still wipes them sooner, as it does any with
// no owner). Never while a user is cached: a signed-in engineer's live list is never put on a clock. Recording the time
// rather than wiping at once is deliberate: an engineer whose old cookie lapsed the day before the deploy keeps
// "Continue offline" for the week, as one whose session ran out after it does.
//
// A SIGN-OUT WIPE THAT FAILS (fix round 4) leaves an unowned time that has already expired ("0", no owner) rather than
// nothing, so the next start or the offline view tries the wipe again, and the password form treats what is left as
// nobody's.
//
// A DIFFERENT person signing in on the device still has them wiped before their own data renders: every sign-in compares
// the new user with the owner of what was kept (OFFLINE_OWNER_KEY below), and the password form also wipes copies that
// record no owner at all. Every sign-in clears both hints, so a sign-in is what stops the expiry clock. A sign-in attempt
// that FAILS (no signal, a mistyped password) wipes nothing, because nobody has signed in (engineerLogin below). None of
// this ever touches the outboxes or the sheet drafts: every wipe here is clearOfflineApiCache (offlineCache.ts).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError } from "./api";
import type { EngineerSelectionStep, Role, User } from "./api";
import { announceLocalSignOut, onSessionEnded, RAN_OUT_END_REASONS } from "./sessionEvents";
import type { SessionEndReason } from "./sessionEvents";
import { clearOfflineApiCache } from "./offlineCache";
import { DRAFT_MAX_AGE_MS } from "./offline/draftStore";
import { getCachedJobs } from "./offline/db";
import { PHONE_VIEW_BLOCKED_EVENT, isInPhonePreviewFrame, noteGesture } from "./phonePreview";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  // True while /api/auth/me is failing for a transient reason (offline, 429, 5xx) but the
  // last-known session is being kept. Drives the quiet connectivity notice.
  connectivityIssue: boolean;
  // Office users sign in with Google (a full-page redirect, handled in the Login
  // page, not here). Engineers sign in with an emailed code (Session BCE); external auditors
  // sign in only with the office's magic link (auditorAccess below; the password route
  // was retired in Session ASB).
  engineerLogin: (username: string, password: string) => Promise<void>;
  // Session BCE: an engineer signs in with their email and an emailed 6-digit code (no password). Same completion as
  // engineerLogin: the server is asked first, and a failed attempt wipes nothing.
  // Resolves with a selection step (Session BCK: an address shared by one subcontractor's engineers) instead of signing in;
  // finish with engineerSelectSignIn. Resolves with nothing when the person is signed in.
  engineerCodeSignIn: (email: string, code: string) => Promise<EngineerSelectionStep | void>;
  engineerSelectSignIn: (email: string, token: string, engineerId: string) => Promise<void>;
  // Magic-link sign-in for an external auditor (Session AEJ, D29): validate a clicked access
  // link, which issues the standard auditor session.
  auditorAccess: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  // Why this browser was last signed out by the server (Session ASB), for the sign-in page to say so; null after a
  // sign-out the person chose, or once they sign in again.
  sessionEndReason: SessionEndReason | null;
  // The role of the person whose session the server just ended (Session ASB review, finding F2), so the engineer sign-in
  // page can offer Google first to an admin or office user driving the engineer app (its password form cannot sign
  // them in). Null alongside sessionEndReason. In memory only: a hint for the page, never a credential.
  endedSessionRole: Role | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const CACHE_KEY = "ele.auth.user";
// Whose offline copies a server-ended session KEPT (F1 above), so the next sign-in by somebody else wipes them before
// they can be shown. A user id, nothing else; removed whenever the copies are wiped or anybody signs in.
const OFFLINE_OWNER_KEY = "ele.auth.offlineOwner";
// WHEN they were kept, in milliseconds since 1970 (the third review), so copies nobody comes back for expire. Written
// and removed with the owner hint; the two are "the hints" below.
const OFFLINE_KEPT_AT_KEY = "ele.auth.offlineKeptAt";

// How long kept copies may wait for somebody to sign in: the sheet drafts' own retention, seven days, by construction
// (offline/draftStore.ts). Past it they are wiped the next time the app starts or the offline view opens.
export const KEPT_OFFLINE_COPIES_MAX_AGE_MS = DRAFT_MAX_AGE_MS;

// THE ONE RULE for whether a session the server ended keeps this browser's offline copies (the header above): only an
// ENGINEER login's, and only when the session ran out, by the one list of reasons that run out (RAN_OUT_END_REASONS,
// sessionEvents.ts, which the history cache's isRevocation reads too). An allow-list, and sessionEvents passes a reason
// it does not know through as itself rather than folding it into no_session (fix round 4), so a reason the server adds
// later wipes until somebody decides otherwise. An admin or office login is wiped whatever the reason: its cached job
// list is the oversight list, every active job's access details, not one engineer's own work.
export function keepsOfflineCopies(reason: SessionEndReason, role: Role): boolean {
  return role === "engineer" && RAN_OUT_END_REASONS.has(reason);
}

interface KeptHints {
  owner: string | null;
  keptAt: string | null;
}

function readKeptHints(): KeptHints {
  try {
    return { owner: localStorage.getItem(OFFLINE_OWNER_KEY), keptAt: localStorage.getItem(OFFLINE_KEPT_AT_KEY) };
  } catch {
    return { owner: null, keptAt: null };
  }
}

// The time first, so a storage failure part way can leave a clock with no owner (the password form then wipes the
// copies at the next sign-in, as it does any with no owner) but never an owner with no clock.
function writeKeptHints(ownerId: string, at: number): void {
  try {
    localStorage.setItem(OFFLINE_KEPT_AT_KEY, String(at));
    localStorage.setItem(OFFLINE_OWNER_KEY, ownerId);
  } catch {
    /* best-effort hint, like the cached user */
  }
}

function clearKeptHints(): void {
  try {
    localStorage.removeItem(OFFLINE_OWNER_KEY);
    localStorage.removeItem(OFFLINE_KEPT_AT_KEY);
  } catch {
    /* best-effort hint, like the cached user */
  }
}

// Have copies a server-ended session kept waited longer than KEPT_OFFLINE_COPIES_MAX_AGE_MS with nobody signing in?
// Exactly seven days is still in time, as it is for a draft. A stamp that is missing (an owner with no clock: a build
// from before the third review) or cannot be read counts as expired, the drafts' own rule (draftStore.ts isStale):
// copies whose age cannot be told cannot be shown to be recent, and the safe direction is to drop them.
function keptCopiesExpired(hints: KeptHints, now: number): boolean {
  if (hints.owner === null && hints.keptAt === null) return false; // nothing kept, or somebody has signed in since
  const keptAt = hints.keptAt === null ? Number.NaN : Number(hints.keptAt);
  if (!Number.isFinite(keptAt)) return true;
  return now - keptAt > KEPT_OFFLINE_COPIES_MAX_AGE_MS;
}

// The expiry wipe in progress, if any, so the app starting and the offline view opening at the same moment share ONE
// wipe, and the view waits for it rather than reading the copies while they are being removed.
let keptCopiesExpiry: Promise<void> | null = null;

// Wipe kept copies that nobody has come back for (the header above). Called as the app starts (AuthProvider's mount) and
// when the offline view opens (OfflineJobs awaits it before reading). Resolves once the wipe has been tried; never
// rejects. The wipe is clearOfflineApiCache, so the outboxes and the sheet drafts are never touched. The hints are
// removed only once the wipe reports that it FINISHED, and only if they are still the ones it was for: a wipe that failed
// (a storage error: clearOfflineApiCache never throws, it answers false) or never finished (the app closed part way)
// leaves them, so the next start or the next opening of the offline view tries again. With nothing recorded at all, it
// puts copies nobody owns on the clock instead (stampUnrecordedCopies).
export function expireKeptOfflineCopies(now: number = Date.now()): Promise<void> {
  if (keptCopiesExpiry) return keptCopiesExpiry;
  const hints = readKeptHints();
  if (!keptCopiesExpired(hints, now)) {
    return hints.owner === null && hints.keptAt === null ? stampUnrecordedCopies(now) : Promise.resolve();
  }
  keptCopiesExpiry = clearOfflineApiCache()
    .then(
      (finished) => {
        if (!finished) return;
        const current = readKeptHints();
        if (current.owner === hints.owner && current.keptAt === hints.keptAt) clearKeptHints();
      },
      // clearOfflineApiCache reports a failure in its result and does not reject. Should that ever change, this keeps
      // THIS function from rejecting, and keeps the hints for the next try as any other failure does.
      () => undefined,
    )
    .finally(() => {
      keptCopiesExpiry = null;
    });
  return keptCopiesExpiry;
}

// Copies saved on this device that carry no hint at all (the header above: a phone an earlier build signed out) are put
// on the expiry clock: the time is recorded, with no owner. Only with nobody signed in on this browser and a saved job
// list to protect (the job list is what the unguarded offline view reads). Both are checked again once the list has
// been read, so a sign-in, or a kept set, that lands meanwhile is never put on a clock. Best-effort and never rejects:
// a device whose IndexedDB cannot be read has nothing the offline view can show either.
async function stampUnrecordedCopies(now: number): Promise<void> {
  if (readCachedUser() !== null) return;
  let saved = 0;
  try {
    saved = (await getCachedJobs()).length;
  } catch {
    return;
  }
  if (saved === 0 || readCachedUser() !== null) return;
  const hints = readKeptHints();
  if (hints.owner !== null || hints.keptAt !== null) return;
  try {
    localStorage.setItem(OFFLINE_KEPT_AT_KEY, String(now));
  } catch {
    /* best-effort hint, like the cached user */
  }
}

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(u: User | null): void {
  try {
    if (u) localStorage.setItem(CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* private mode / storage disabled: the cache is a best-effort hint only */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Optimistic hydrate from the last-known session so a cold offline open never flashes the login
  // screen before refresh() has a chance to confirm the session.
  const [user, setUser] = useState<User | null>(() => readCachedUser());
  const [loading, setLoading] = useState(true);
  const [connectivityIssue, setConnectivityIssue] = useState(false);
  const [sessionEndReason, setSessionEndReason] = useState<SessionEndReason | null>(null);
  const [endedSessionRole, setEndedSessionRole] = useState<Role | null>(null);
  // Mirrors `user` for the session-ended listener, which must not re-subscribe on every change.
  const userRef = useRef<User | null>(user);
  userRef.current = user;
  // When this browser last signed in, so a 401 for a request sent BEFORE that (a drain still in flight while the
  // engineer signed in again) is not mistaken for the new session ending.
  const lastSignInAt = useRef(0);

  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attempt = useRef(0);
  const mounted = useRef(true);

  const clearRetry = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  // The ONE local sign-out cleanup: logout() runs it after its network call, and a session the server ended runs it
  // with no network call at all. The request cache is cleared explicitly, through the local sign-out signal api.ts
  // listens to (it used to rely on the logout POST succeeding), and clearOfflineApiCache drops the offline reference
  // data while never touching the outboxes or the job sheet drafts. keepOfflineData (an engineer's session that ran out,
  // keepsOfflineCopies above) skips that last step and records whose copies were kept, and when, so they can expire.
  // A wipe that reports it did NOT finish leaves an unowned time that has already expired (the header above), so the
  // next start or the offline view tries again; unless somebody has signed in on this browser meanwhile, whose own live
  // copies must never be put on a clock.
  const signOutLocally = useCallback(
    async (opts: { keepOfflineData?: boolean } = {}) => {
      const previous = userRef.current;
      clearRetry();
      userRef.current = null;
      setUser(null);
      writeCachedUser(null);
      setConnectivityIssue(false);
      announceLocalSignOut();
      if (opts.keepOfflineData) {
        if (previous) writeKeptHints(previous.id, Date.now());
        return;
      }
      clearKeptHints();
      const finished = await clearOfflineApiCache();
      if (!finished && readCachedUser() === null) {
        try {
          localStorage.setItem(OFFLINE_KEPT_AT_KEY, "0");
        } catch {
          /* best-effort hint, like the cached user */
        }
      }
    },
    [clearRetry],
  );

  // Before a user's data is rendered, drop offline copies a server-ended session kept for SOMEBODY ELSE (F1 above). A
  // sign-in always clears both hints: whatever was kept is now either this person's own or gone.
  const wipeIfKeptForSomeoneElse = useCallback((userId: string) => {
    const { owner } = readKeptHints();
    clearKeptHints();
    if (owner && owner !== userId) void clearOfflineApiCache();
  }, []);

  const markSignedIn = useCallback(() => {
    lastSignInAt.current = Date.now();
    setSessionEndReason(null);
    setEndedSessionRole(null);
  }, []);

  // A session the server ended (sessionEvents.ts, reported by api.ts): sign out here too, and remember why (and whose
  // it was, for the engineer sign-in page). An engineer's session that ran out keeps the offline copies; every other
  // ending wipes them (keepsOfflineCopies, the header above).
  useEffect(
    () =>
      onSessionEnded((event) => {
        const ended = userRef.current;
        if (!ended) return; // nobody is signed in on this browser: nothing to end
        if (event.startedAt < lastSignInAt.current) return; // an answer about the session before this one
        setSessionEndReason(event.reason);
        setEndedSessionRole(ended.role);
        void signOutLocally({ keepOfflineData: keepsOfflineCopies(event.reason, ended.role) });
      }),
    [signOutLocally],
  );

  // Kept copies that have waited more than seven days for somebody to sign in go as the app starts (the header above).
  // Declared before the refresh effect below, so the wipe has begun before anything is fetched.
  useEffect(() => {
    void expireKeptOfflineCopies();
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await api.me();
      if (!mounted.current) return;
      // A DIFFERENT user than the device last held means someone else has signed in, and the
      // office Google sign-in is a full-page redirect that resolves here rather than through
      // engineerLogin, so this is the only place that path can be caught (Session AEK). Drop the
      // previous user's offline caches before their data can be rendered to this one.
      const previous = readCachedUser();
      if (previous && previous.id !== res.user.id) void clearOfflineApiCache();
      // And offline copies a server-ended session kept (F1): the cached user was removed with that session, so the
      // check above cannot see whose they were.
      wipeIfKeptForSomeoneElse(res.user.id);
      setUser(res.user);
      writeCachedUser(res.user);
      setConnectivityIssue(false);
      attempt.current = 0;
      clearRetry();
    } catch (err) {
      if (!mounted.current) return;
      if (err instanceof ApiError && err.status === 401) {
        // Genuinely signed out: the server rejected the session cookie.
        setUser(null);
        writeCachedUser(null);
        setConnectivityIssue(false);
        attempt.current = 0;
        clearRetry();
      } else {
        // Transient (429, 5xx, offline/network TypeError): keep the last-known session and retry
        // with backoff. Never bounce an authenticated user to login over a blip.
        setUser((u) => u ?? readCachedUser());
        setConnectivityIssue(true);
        if (!retryTimer.current) {
          const n = Math.min(attempt.current, 5);
          const delay = Math.min(1000 * 2 ** n, 30000);
          attempt.current = n + 1;
          retryTimer.current = setTimeout(() => {
            retryTimer.current = null;
            void refresh();
          }, delay);
        }
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [clearRetry, wipeIfKeptForSomeoneElse]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
      clearRetry();
    };
  }, [refresh, clearRetry]);

  // The engineer password sign-in (Session ASB, the review of the fixes, finding 1). The SERVER IS ASKED FIRST. A
  // failed attempt (a fetch with no signal, or a mistyped password) throws here and wipes nothing: the copies a session
  // that ran out KEPT (F1 above) are still there for "Continue offline", which is exactly where an engineer who cannot
  // sign in underground goes next. The wipe used to run before the request, so one failed attempt emptied the job list
  // and access details although nobody had signed in.
  //
  // Once the server has said WHO signed in, copies kept for anybody else go before this person's data renders (the
  // wipe is awaited before setUser), so a shared device never shows one engineer another's jobs. The test is stricter
  // than the Google and auditor sign-ins' (wipeIfKeptForSomeoneElse): copies that record NO owner are wiped too, since
  // nothing says whose they are (an older build wrote no owner hint). The same engineer signing back in keeps them, and
  // either way both hints go: the copies are no longer kept, so they no longer expire.
  const engineerLogin = useCallback(async (username: string, password: string) => {
    const res = await api.engineerLogin(username, password);
    if (readKeptHints().owner !== res.user.id) await clearOfflineApiCache();
    clearKeptHints();
    markSignedIn();
    setUser(res.user);
    writeCachedUser(res.user);
    setConnectivityIssue(false);
  }, [markSignedIn]);

  const finishEngineerCodeSignIn = useCallback(async (signedIn: User) => {
    if (readKeptHints().owner !== signedIn.id) await clearOfflineApiCache();
    clearKeptHints();
    markSignedIn();
    setUser(signedIn);
    writeCachedUser(signedIn);
    setConnectivityIssue(false);
  }, [markSignedIn]);

  const engineerCodeSignIn = useCallback(async (email: string, code: string): Promise<EngineerSelectionStep | void> => {
    const res = await api.engineerSignIn.verifyCode(email, code);
    if ("select" in res) return res.select;
    await finishEngineerCodeSignIn(res.user);
  }, [finishEngineerCodeSignIn]);

  const engineerSelectSignIn = useCallback(async (email: string, token: string, engineerId: string) => {
    const res = await api.engineerSignIn.selectEngineer(email, token, engineerId);
    await finishEngineerCodeSignIn(res.user);
  }, [finishEngineerCodeSignIn]);

  const auditorAccess = useCallback(async (token: string) => {
    const res = await api.auditorAccess(token);
    wipeIfKeptForSomeoneElse(res.user.id);
    markSignedIn();
    setUser(res.user);
    writeCachedUser(res.user);
    setConnectivityIssue(false);
  }, [markSignedIn, wipeIfKeptForSomeoneElse]);

  const logout = useCallback(async () => {
    // THE PHONE VIEW NEVER SIGNS OUT (Session BDZ). A page in the Phone view's frame shares this browser's cookie, its
    // offline API cache and its cached user with the real window, so a sign-out pressed there, even with the request
    // refused, would still empty those for everyone. The notice says so and nothing else happens.
    if (isInPhonePreviewFrame()) {
      noteGesture();
      window.dispatchEvent(new Event(PHONE_VIEW_BLOCKED_EVENT));
      return;
    }
    // Clear the local session regardless of the API outcome: the client must never keep
    // trusting a session it has asked to end (a failed/again-network logout POST would
    // otherwise leave the user object populated and the route guard would re-admit them).
    clearRetry();
    try {
      await api.logout();
    } finally {
      // A sign-out the person chose carries no "your session ended" reason.
      setSessionEndReason(null);
      setEndedSessionRole(null);
      // Drops the offline diary cache so the next user on a shared device starts clean.
      await signOutLocally();
    }
  }, [clearRetry, signOutLocally]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, connectivityIssue, engineerLogin, engineerCodeSignIn, engineerSelectSignIn, auditorAccess, logout, refresh, sessionEndReason, endedSessionRole }),
    [user, loading, connectivityIssue, engineerLogin, engineerCodeSignIn, engineerSelectSignIn, auditorAccess, logout, refresh, sessionEndReason, endedSessionRole],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {connectivityIssue && user && <ConnectivityNotice />}
    </AuthContext.Provider>
  );
}

// A quiet, non-blocking notice that the connection is down and the session is being kept. Shown
// only to an authenticated user (never on the public pages), and cleared the moment a refresh
// succeeds or the session is genuinely ended.
function ConnectivityNotice(): ReactNode {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-3 left-1/2 z-50 -translate-x-1/2 rounded-ele border border-[#b8860b] bg-ele-bg px-3 py-1.5 text-xs text-[#b8860b] shadow-lg"
    >
      Connection lost. Working offline.
    </div>
  );
}

/**
 * The signed-in user where a component can also render outside the provider (Session BDG2: the service record panel is mounted
 * by many pages and tests, and only needs the role to decide whether to offer an admin-only action). Null outside a provider;
 * the server decides every permission regardless.
 */
export function useOptionalAuthUser(): AuthContextValue["user"] | null {
  return useContext(AuthContext)?.user ?? null;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
